import { PreviewProps } from './types';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { FormattedMessageHelper } from '../../shared/helpers/internationalization';
import * as React from 'react';
import { StateType } from '../../reducers';
import { getUserDataSelector } from '../../reducers/AuthenticationReducer/AuthenticationSelector';
import { connect } from 'react-redux';
import AvatarEditor from 'react-avatar-editor';
import { Modal } from 'semantic-ui-react';

export function ImagePreview({ data, files, onRemoveFile, onUpdateFile }: PreviewProps) {
  const selectedFile = files?.[0];

  const [preview, setPreview] = useState<string | undefined>();

  const [isCropModalOpen, setIsCropModalOpen] = useState(false);

  useEffect(() => {
    if (!selectedFile) {
      setPreview(undefined);
      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  const isImageUploaded = data && data.getIn(['image']);

  const handleCropNew = () => setIsCropModalOpen(true);
  const handleCloseCropModal = () => setIsCropModalOpen(false);
  const handleRemoveNew = () => onRemoveFile();
  const handleCropResult = (newFile: File) => onUpdateFile([newFile]);

  if (isImageUploaded || preview) {
    const imageUrl = data.getIn(['image', 'url']) + `?rand=` + Math.random(); //needed to force reload the image after uploading a new one
    return (
      <div className="input-file__image-preview-section">
        {isCropModalOpen && preview && <ImageCropModal image={preview} onClose={handleCloseCropModal} onCrop={handleCropResult} />}
        {isImageUploaded && (
          <div className="input-file__image-preview-container">
            <div>
              <FormattedMessageHelper id="account.photo.current" />
            </div>
            <img className="input-file__image-preview" src={imageUrl}></img>
          </div>
        )}
        {preview && (
          <div className="input-file__image-preview-container">
            <div>
              <FormattedMessageHelper id="account.photo.new" />
            </div>

            <img className="input-file__image-preview" src={preview}></img>
            <div className="input-file__image-preview-buttons">
              <button className="button button--secondary button--small" onClick={handleCropNew}>
                <FormattedMessageHelper id="account.crop" />
              </button>
              <button className="button button--secondary button--small" onClick={handleRemoveNew}>
                <FormattedMessageHelper id="account.photo.remove" />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="input-file__no-image">
      <FormattedMessageHelper id="account.photo.no" />
    </div>
  );
}

interface ImageCropModalProps {
  image: string;
  onClose: () => void;
  onCrop: (file: File) => void;
}

function ImageCropModal({ image, onClose, onCrop }: ImageCropModalProps) {
  const setEditorRef = useRef<AvatarEditor>(null);
  const [scale, setScale] = useState(1.2);

  const handleClose = () => {
    onClose();
  };

  const handleScale = (e: ChangeEvent<HTMLInputElement>) => {
    const newScale = parseFloat(e.target.value);
    setScale(newScale);
  };

  const handleCrop = () => {
    if (setEditorRef && setEditorRef.current) {
      const canvas = setEditorRef.current.getImageScaledToCanvas();
      canvas.toBlob(
        blob => {
          if (!blob) {
            return;
          }
          const file = new File([blob], 'cropped-avatar.jpg', { type: 'image/jpeg' });
          onCrop(file);
        },
        'image/jpeg',
        0.8
      );
    }
    handleClose();
  };

  return (
    <Modal open={true} size="tiny" className="crop-modal">
      <Modal.Header>
        <FormattedMessageHelper id="account.crop.header" />
      </Modal.Header>
      <Modal.Content>
        <div className="crop-modal__editor-container">
          <AvatarEditor
            ref={setEditorRef}
            image={image}
            width={400}
            height={400}
            border={50}
            color={[255, 255, 255, 0.6]} // RGBA
            scale={scale}
            rotate={0}
          />
        </div>
        <div className="crop-modal__zoom-container">
          <FormattedMessageHelper id="account.crop.zoom" />
          <input
            className="crop-modal__zoom"
            name="scale"
            type="range"
            onChange={handleScale}
            min="1"
            max="5"
            step="0.01"
            defaultValue="1.2"
          />
        </div>
        <div className="crop-modal__buttons">
          <button className="button button--primary" onClick={handleCrop}>
            <FormattedMessageHelper id="account.crop" />
          </button>
          <button className="button button--secondary" onClick={handleClose}>
            <FormattedMessageHelper id="account.crop.close" />
          </button>
        </div>
      </Modal.Content>
    </Modal>
  );
}

const mapPreviewStateToProps = (state: StateType): Pick<PreviewProps, 'data'> => ({
  data: getUserDataSelector(state),
});

export const PreviewConnect = connect<Pick<PreviewProps, 'data'>, {}, any, StateType>(mapPreviewStateToProps, {})(ImagePreview);
